import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Container, Typography, Grid, InputBase, Paper,
  Stack, Pagination, Alert,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { Search as SearchIcon, School, Route } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCatalog } from '../../context/CatalogContext';
import ContentCard from '../../components/common/ContentCard';
import LoadingSkeleton from '../../components/common/LoadingSkeleton';
import usePagination from '../../hooks/usePagination';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import useIsMobile from '../../hooks/useIsMobile';

const PAGE_SIZE = 12;

export default function SearchPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { modules, learningPaths, loadModules, loadLearningPaths, loading, error } = useCatalog();

  const params = new URLSearchParams(location.search);
  const initialQ = params.get('q') || '';

  const [query, setQuery] = useState(initialQ);
  const [inputValue, setInputValue] = useState(initialQ);
  const [contentType, setContentType] = useState('all');

  useEffect(() => {
    if (modules.length === 0) loadModules();
    if (learningPaths.length === 0) loadLearningPaths();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q') || '';
    setQuery(q);
    setInputValue(q);
  }, [location.search]);

  const results = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    const match = (item) =>
      item.title?.toLowerCase().includes(q) ||
      item.summary?.toLowerCase().includes(q) ||
      (item.products || []).some((p) => p.toLowerCase().includes(q)) ||
      (item.roles || []).some((r) => r.toLowerCase().includes(q)) ||
      (item.subjects || []).some((s) => s.toLowerCase().includes(q));

    const mods = contentType !== 'paths' ? modules.filter(match).map((m) => ({ ...m, _type: 'module' })) : [];
    const paths = contentType !== 'modules' ? learningPaths.filter(match).map((p) => ({ ...p, _type: 'path' })) : [];
    return [...mods, ...paths].sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
  }, [query, modules, learningPaths, contentType]);

  const pagination = usePagination(results, PAGE_SIZE);
  const infinite   = useInfiniteScroll(results, PAGE_SIZE);

  const handleSearch = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(inputValue.trim())}`);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Search bar */}
      <Box component="form" onSubmit={handleSearch} sx={{ mb: 4 }}>
        <Paper
          sx={{
            display: 'flex', alignItems: 'center',
            px: 2.5, py: 1.25,
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            '&:focus-within': { borderColor: '#0078d4', boxShadow: '0 0 0 3px rgba(0,120,212,0.1)' },
          }}
        >
          <SearchIcon sx={{ color: 'text.disabled', mr: 1.5, fontSize: 22 }} />
          <InputBase
            fullWidth
            autoFocus={!query}
            placeholder="Search modules, learning paths, products..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            sx={{ fontSize: '1rem' }}
          />
        </Paper>
      </Box>

      {/* Results header */}
      {query && (
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 3 }} spacing={2}>
          <Box>
            <Typography variant="h5" fontWeight={800} letterSpacing="-0.5px">
              {loading ? 'Searching...' : `${results.length} results`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              for &ldquo;<strong>{query}</strong>&rdquo;
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={contentType}
            exclusive
            onChange={(_, v) => { if (v) setContentType(v); pagination.reset && pagination.reset(); }}
            size="small"
            sx={{ '& .MuiToggleButton-root': { borderRadius: '8px', textTransform: 'none', px: 2 } }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="modules">
              <School sx={{ mr: 0.5, fontSize: 16 }} /> Modules
            </ToggleButton>
            <ToggleButton value="paths">
              <Route sx={{ mr: 0.5, fontSize: 16 }} /> Paths
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {!query && (
        <Box sx={{ textAlign: 'center', py: 12 }}>
          <SearchIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">Start typing to search</Typography>
          <Typography color="text.disabled" sx={{ mt: 1 }}>Search across modules and learning paths</Typography>
        </Box>
      )}

      {query && loading && (modules.length === 0 || learningPaths.length === 0) ? (
        <LoadingSkeleton count={PAGE_SIZE} />
      ) : query && results.length === 0 && !loading ? (
        <Box sx={{ textAlign: 'center', py: 12 }}>
          <Typography variant="h6" color="text.secondary">No results for &ldquo;{query}&rdquo;</Typography>
          <Typography color="text.disabled" sx={{ mt: 1 }}>Try different keywords or browse all content</Typography>
        </Box>
      ) : query && (
        <>
          {!isMobile && (
            <>
              <Grid container spacing={2.5}>
                {pagination.pageItems.map((item) => (
                  <Grid key={item.uid} item xs={12} sm={6} md={4} lg={3}>
                    <ContentCard item={item} variant={item._type === 'path' ? 'path' : 'module'} />
                  </Grid>
                ))}
              </Grid>
              {pagination.totalPages > 1 && (
                <Stack alignItems="center" sx={{ mt: 5 }}>
                  <Pagination count={pagination.totalPages} page={pagination.page}
                    onChange={(_, p) => pagination.goTo(p)} color="primary" shape="rounded" size="large" />
                </Stack>
              )}
            </>
          )}
          {isMobile && (
            <>
              <Grid container spacing={2}>
                {infinite.visibleItems.map((item) => (
                  <Grid key={item.uid} item xs={12} sm={6}>
                    <ContentCard item={item} variant={item._type === 'path' ? 'path' : 'module'} />
                  </Grid>
                ))}
              </Grid>
              <div ref={infinite.sentinelRef} style={{ height: 40, marginTop: 16 }} />
              {!infinite.hasMore && results.length > 0 && (
                <Typography align="center" color="text.disabled" sx={{ mt: 2, pb: 4 }}>
                  All {results.length} results shown
                </Typography>
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
}
